export const VOLUNTEER_LOCALES = ["en", "nl", "fr"];
export const VOLUNTEER_PAGE_STATUSES = ["draft", "open", "closed"];
export const VOLUNTEER_SHIFT_VISIBILITIES = ["public", "hidden"];
export const VOLUNTEER_ASSIGNMENT_STATUSES = ["confirmed", "pending", "waitlisted", "cancelled"];
/**
 * How a page confirms a self-signup's email (`volunteer_signup_pages.verification_mode`,
 * CHECK-pinned to these three; default `save_then_confirm`):
 *  - `link_first`        the visitor requests a link by email first and signs up from it;
 *  - `save_then_confirm` one-step signup, saved and flagged unverified until the
 *                        volunteer opens the emailed link or an organizer confirms;
 *  - `none`              one-step signup treated as verified (the click is still recorded).
 */
export const VOLUNTEER_VERIFICATION_MODES = ["link_first", "save_then_confirm", "none"];
/**
 * The TS mirror of SQL `volunteer_is_effectively_verified`: verified by the
 * link / an organizer (`verified_at` set), or the page needs no confirmation.
 * The staff overview fills `VolunteerWire.effectively_verified` with it, so
 * the web never re-derives the rule.
 */
export function isVolunteerEffectivelyVerified(volunteer, mode) {
    return volunteer.verified_at !== null || mode === "none";
}
/** Statuses that hold a place on a shift's roster (the partial unique index's `status <> 'cancelled'`). */
export const VOLUNTEER_LIVE_STATUSES = ["confirmed", "pending", "waitlisted"];
/**
 * The per-shift result of a claim (`volunteer_claim_internal`) — data, not an
 * error. `confirmed` / `pending` / `waitlisted` wrote an assignment; the rest
 * did not:
 *  - `full`     no room and the volunteer did not ask for the waitlist;
 *  - `past`     the shift has started (venue now);
 *  - `hidden`   not public, or its role is closed to self-signup;
 *  - `declined` an organiser cancelled or declined this volunteer on this shift;
 *  - `already`  the volunteer already holds a live row on it;
 *  - `overlap`  it overlaps another of the volunteer's shifts (or planner items).
 */
export const VOLUNTEER_CLAIM_OUTCOMES = [
    "confirmed",
    "pending",
    "waitlisted",
    "full",
    "past",
    "hidden",
    "declined",
    "already",
    "overlap",
];
// ---------------------------------------------------------------------------
// Email jobs (RPC → API only; never returned to the browser)
// ---------------------------------------------------------------------------
export const VOLUNTEER_EMAIL_KINDS = [
    "magic_link",
    "claim_outcome",
    "waitlist_promoted",
    "approval_outcome",
    /** The one-step signup's result (`link` = the freshly minted raw link). */
    "signup_verify",
    /** A one-step signup with a known address: nothing saved, their link + the picked shifts. */
    "already_signed_up",
    /**
     * The per-role welcome (20261010110000): never a job's own kind — one
     * extra email per entry of a job's `welcomes`; listed here as an email
     * (and metering) kind.
     */
    "role_welcome",
    /** The organiser released the waitlist (20261021180000): the shifts let go (no outcome), a button to pick another. */
    "waitlist_released",
];
export const VOLUNTEER_EMAIL_JOB_KINDS = VOLUNTEER_EMAIL_KINDS.filter((k) => k !== "role_welcome");
// ---------------------------------------------------------------------------
// Coded errors (the web reads `err.envelope.code`)
// ---------------------------------------------------------------------------
export const VOLUNTEER_ERROR_CODES = {
    /** 404 — bad / unknown / draft / flag-off token, or a bad link on /me /claim /cancel (uniform). */
    pageNotFound: "volunteer_page_not_found",
    /**
     * 409 — RV008: an intake edit that would damage existing answers (retype,
     * re-flag sensitive, drop an answered question or one of its option / row /
     * column ids). The body carries `question_id`, `question_label` and `reason`
     * (`VolunteerIntakeChangeRefusedReason`).
     */
    intakeChangeRefused: "volunteer_intake_change_refused",
    /** 409 — RV009: too close to the shift to cancel. */
    cancelCutoff: "volunteer_cancel_cutoff",
    /** 409 — RV010: the page is not open, or today is outside opens_on..closes_on. */
    pageClosed: "volunteer_page_closed",
    /** 409 — RV011: the latest code of conduct was not accepted. */
    consentRequired: "volunteer_consent_required",
    /** 400 — RV012: name missing, or phone missing when the page requires it. */
    profileInvalid: "volunteer_profile_invalid",
    /** 409 — a staff assign / confirm needs `force` (body carries `reason`). */
    needsConfirmation: "volunteer_needs_confirmation",
    /** 409 — role or shift delete while live assignments exist. */
    hasAssignments: "volunteer_has_assignments",
    /** 409 — a volunteer edit to an email another volunteer on the project uses. */
    emailTaken: "volunteer_email_taken",
    /** 409 — RV013: a one-step signup on a `link_first` page (request a link instead). */
    signupNeedsLink: "volunteer_signup_needs_link",
    // Volunteer messages (20261021120000; schemas/volunteerMessages.ts).
    /** 403 — RV021: the caller may not send this message (not an admin / a lead of the role; everyone is admin-only). */
    messageNotAllowed: "volunteer_message_not_allowed",
    /** 429 — RV022: the rolling 24 h limit (5 per role, 3 to everyone); the body carries `retry_at` and `limit`. */
    messageLimit: "volunteer_message_limit",
    /** 409 — RV023: nobody to send to (no confirmed volunteer with a usable address). */
    messageNoRecipients: "volunteer_message_no_recipients",
    /** 422 — RV024: more than 500 recipients; the body carries `recipient_count` and `cap`. Refused, never cut short. */
    messageTooMany: "volunteer_message_too_many",
    /** 503 — messaging is not configured on this deployment (no notify sender / configuration set / public API URL). */
    messagingUnavailable: "volunteer_messaging_unavailable",
    /** 502 — a test email (POST …/messages/test) could not be sent (SES refused after retries). */
    messageTestFailed: "volunteer_message_test_failed",
    /** 404 — the uniform unsubscribe answer (malformed / unknown token, erased volunteer). */
    unsubscribeNotFound: "volunteer_unsubscribe_not_found",
};
/** The feature's custom SQLSTATEs → HTTP status + coded envelope. */
export const VOLUNTEER_SQLSTATE_ERRORS = {
    RV008: { status: 409, code: VOLUNTEER_ERROR_CODES.intakeChangeRefused },
    RV009: { status: 409, code: VOLUNTEER_ERROR_CODES.cancelCutoff },
    RV010: { status: 409, code: VOLUNTEER_ERROR_CODES.pageClosed },
    RV011: { status: 409, code: VOLUNTEER_ERROR_CODES.consentRequired },
    RV012: { status: 400, code: VOLUNTEER_ERROR_CODES.profileInvalid },
    RV013: { status: 409, code: VOLUNTEER_ERROR_CODES.signupNeedsLink },
};
/** Why `volunteer_signup_intake_guard` refused an intake edit (RV008). */
export const VOLUNTEER_INTAKE_CHANGE_REFUSED_REASONS = [
    "question_removed",
    "type_changed",
    "sensitive_changed",
    "option_removed",
    "row_removed",
    "column_removed",
    "duplicate_id",
    "range_narrowed",
];
const INTAKE_CHANGE_REFUSED_PREFIX = "VOLUNTEER_INTAKE_CHANGE_REFUSED|";
/**
 * Parse the RV008 message `VOLUNTEER_INTAKE_CHANGE_REFUSED|<question id>|<reason>|<label>`
 * (split on the first three `|` only — the label is last and may contain `|`).
 * Null for any other message, an unknown reason or an empty id / label.
 */
export function parseIntakeChangeRefused(message) {
    if (typeof message !== "string")
        return null;
    const start = message.indexOf(INTAKE_CHANGE_REFUSED_PREFIX);
    if (start === -1)
        return null;
    const rest = message.slice(start + INTAKE_CHANGE_REFUSED_PREFIX.length);
    const first = rest.indexOf("|");
    const second = first === -1 ? -1 : rest.indexOf("|", first + 1);
    if (second === -1)
        return null;
    const questionId = rest.slice(0, first);
    const reason = rest.slice(first + 1, second);
    const label = rest.slice(second + 1);
    if (!questionId || !label)
        return null;
    if (!VOLUNTEER_INTAKE_CHANGE_REFUSED_REASONS.includes(reason))
        return null;
    return { questionId, reason: reason, label };
}
//# sourceMappingURL=volunteerSignupReads.js.map